import { GLOSSARY } from '../../data/glossary';
import type { GlossaryTerm } from '../../data/glossary';

// Matcher utilities keyed off the canonical GLOSSARY array. These power both the
// inline <GlossaryTerm> auto-linking inside rendered markdown and the index page.
//
// Design notes:
//  - Lookups are by lowercase slug AND lowercase surface term, so a rehype pass
//    that finds a word boundary can resolve the canonical entry cheaply.
//  - The scanner matches case-insensitively on word boundaries, LONGEST term
//    first (so "alkaline earth metals" wins over "metals"), and the caller wraps
//    only the FIRST occurrence per rendered block to avoid noise.

// slug -> term (the canonical record).
const BY_SLUG: Map<string, GlossaryTerm> = new Map(
  GLOSSARY.map((t) => [t.slug, t]),
);

// lowercased surface form -> term, for resolving a matched substring.
const BY_LOWER_TERM: Map<string, GlossaryTerm> = new Map(
  GLOSSARY.map((t) => [t.term.toLowerCase(), t]),
);

export function getTermBySlug(slug: string): GlossaryTerm | undefined {
  return BY_SLUG.get(slug);
}

export function getTermByText(text: string): GlossaryTerm | undefined {
  return BY_LOWER_TERM.get(text.trim().toLowerCase());
}

// Escape a term for safe inclusion in a RegExp alternation.
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Terms sorted longest-first so the alternation prefers the most specific match.
const TERMS_LONGEST_FIRST = [...GLOSSARY]
  .map((t) => t.term)
  .sort((a, b) => b.length - a.length);

// A single global, case-insensitive regex. Each surface term is bounded so we
// only match whole words/phrases (e.g. "ion" never fires inside "cation").
// We can't always use \b because some terms end in non-word chars, but every
// GLOSSARY term is alphabetic, so \b on both ends is correct and simplest.
const MATCH_RE = new RegExp(
  `\\b(?:${TERMS_LONGEST_FIRST.map(escapeRegExp).join('|')})\\b`,
  'gi',
);

export interface TermMatch {
  /** The matched surface text, exactly as it appeared in the source. */
  text: string;
  /** Start offset within the scanned string. */
  start: number;
  /** End offset (exclusive). */
  end: number;
  /** The resolved canonical glossary record. */
  term: GlossaryTerm;
}

/**
 * Find glossary-term occurrences in a plain-text string.
 *
 * Returns matches in left-to-right order, non-overlapping. When `firstOnly` is
 * true (the default for markdown auto-linking), at most ONE match per *slug* is
 * returned so a block doesn't repeat the same interactive term.
 */
export function findTermMatches(text: string, firstOnly = true): TermMatch[] {
  if (!text) return [];
  const out: TermMatch[] = [];
  const seen = new Set<string>();
  // Reset lastIndex on the shared regex before each scan.
  MATCH_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MATCH_RE.exec(text)) !== null) {
    const surface = m[0];
    const term = getTermByText(surface);
    // The regex alternation can only match known terms, but guard anyway.
    if (!term) continue;
    if (firstOnly && seen.has(term.slug)) continue;
    seen.add(term.slug);
    out.push({ text: surface, start: m.index, end: m.index + surface.length, term });
  }
  return out;
}
